import axios from "axios";

// Orders are served through the API gateway which routes to the rider service
// API Gateway URL - should be http://localhost:8000/api
const GATEWAY_URL =
  import.meta.env.VITE_RIDER_API_URL || "http://localhost:8000";

// Configure axios instance for orders - routes through gateway to rider service
const api = axios.create({
  baseURL: `${GATEWAY_URL}/orders`,
  headers: {
    // Note: Do NOT set Content-Type globally - FormData and file uploads need to set their own
    // "Content-Type": "application/json", // REMOVED - breaks multipart uploads
  },
});

// Request interceptor to add auth token
api.interceptors.request.use((config) => {
  const token = localStorage.getItem("authToken");
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

export interface Order {
  id: string;
  riderPublicRiderId: string;
  clientId?: string;
  clientName?: string;
  city?: string;
  date?: string;
  weekNumber?: number;
  totalOrdersDay: number;
  createdAt: string;
  updatedAt: string;
}

export interface OrdersResponse {
  data: Order[];
  pagination?: {
    page: number;
    pageSize: number;
    total: number;
    totalPages: number;
  };
}

export interface CreateOrderPayload {
  riderPublicRiderId: string;
  clientId?: string;
  clientName?: string;
  city: string;
  date: string;
  totalOrdersDay: number;
}

export interface OrdersFilters {
  page?: number;
  pageSize?: number;
  riderId?: string;
  clientId?: string;
  city?: string;
  dateFrom?: string;
  dateTo?: string;
}

export interface CitySummary {
  city: string;
  totalOrders: number;
  totalRecords: number;
}

export interface CitySummaryResponse {
  success: boolean;
  summary: CitySummary[];
}

export interface ClientWiseByCityResponse {
  byCity?: Array<{ city: string; clients: Record<string, number> }>;
  byCityMap?: Record<string, Record<string, number>>;
}

export interface FullDataDailyRow {
  date: string;
  riderCount: number;
  evRiderCount: number;
  nonEvRiderCount: number;
  totalOrders: number;
  evOrders: number;
  nonEvOrders: number;
  zeroOrderRiderCount: number;
  totalEarning: number;
  evEarning: number;
  nonEvEarning: number;
  mfAmount: number;
  rent: number;
  totalRevenue: number;
}

export interface FullDataAnalyticsResponse {
  success: boolean;
  data: {
    period: {
      dateFrom: string;
      dateTo: string;
      dates: string[];
    };
    filters: {
      city: string;
      clientName: string;
      cities: string[];
      clients: string[];
    };
    daily: FullDataDailyRow[];
    totals: Omit<FullDataDailyRow, "date">;
  };
}

const ordersService = {
  // Create a new order
  async createOrder(payload: CreateOrderPayload): Promise<Order> {
    try {
      const response = await api.post("/", payload);
      return response.data.order;
    } catch (error: any) {
      throw new Error(
        error.response?.data?.message ||
          error.message ||
          "Failed to create order"
      );
    }
  },

  // GET /api/orders/city/clientwise
  async getClientWiseByCityByName(filters?: { dateFrom?: string; dateTo?: string; city?: string; riderId?: string; clientName?: string; }) : Promise<ClientWiseByCityResponse> {
    try {
      const params = new URLSearchParams();
      if (filters?.dateFrom) params.append("dateFrom", filters.dateFrom);
      if (filters?.dateTo) params.append("dateTo", filters.dateTo);
      if (filters?.city) params.append("city", filters.city);
      if (filters?.riderId) params.append("riderId", filters.riderId);
      if (filters?.clientName) params.append("clientName", filters.clientName);

      const response = await api.get(`/city/clientwise?${params.toString()}`);
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || error.message || "Failed to fetch client-wise city summary");
    }
  },

  // GET /api/orders/monthly/clientcity
  async getMonthlyClientCitySummary(filters?: { dateFrom?: string; dateTo?: string; city?: string; }) {
    try {
      const params = new URLSearchParams();
      if (filters?.dateFrom) params.append("dateFrom", filters.dateFrom);
      if (filters?.dateTo) params.append("dateTo", filters.dateTo);
      if (filters?.city) params.append("city", filters.city);
      const response = await api.get(`/monthly/clientcity?${params.toString()}`);
      return response.data; // array of { city, months: { YYYY-MM: { clientName: total } } }
    } catch (error: any) {
      throw new Error(error.response?.data?.message || error.message || "Failed to fetch monthly client-city summary");
    }
  },

  async getFullDataAnalytics(filters: {
    dateFrom: string;
    dateTo: string;
    city?: string;
    clientName?: string;
  }): Promise<FullDataAnalyticsResponse> {
    try {
      const params = new URLSearchParams();
      params.append("dateFrom", filters.dateFrom);
      params.append("dateTo", filters.dateTo);
      if (filters.city) params.append("city", filters.city);
      if (filters.clientName) params.append("clientName", filters.clientName);

      const response = await api.get(`/analytics/full-data?${params.toString()}`);
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || error.message || "Failed to fetch full data analytics");
    }
  },

  // Get all orders with optional filters
  async getOrders(filters?: OrdersFilters): Promise<OrdersResponse> {
    try {
      const params = new URLSearchParams();
      if (filters?.page) params.append("page", filters.page.toString());
      if (filters?.pageSize)
        params.append("pageSize", filters.pageSize.toString());
      if (filters?.riderId) params.append("riderId", filters.riderId);
      if (filters?.clientId) params.append("clientId", filters.clientId);
      if (filters?.city) params.append("city", filters.city);
      if (filters?.dateFrom) params.append("dateFrom", filters.dateFrom);
      if (filters?.dateTo) params.append("dateTo", filters.dateTo);

      const response = await api.get(`/?${params.toString()}`);
      return response.data;
    } catch (error: any) {
      console.error("Error fetching orders:", error);
      throw new Error(
        error.response?.data?.message ||
          error.message ||
          "Failed to fetch orders"
      );
    }
  },

  // Get order by ID
  async getOrderById(orderId: string): Promise<Order> {
    try {
      const response = await api.get(`/${orderId}`);
      return response.data;
    } catch (error: any) {
      throw new Error(
        error.response?.data?.message ||
          error.message ||
          "Failed to fetch order"
      );
    }
  },

  // Update order
  async updateOrder(
    orderId: string,
    updates: Partial<Order>
  ): Promise<Order> {
    try {
      const response = await api.patch(`/${orderId}`, updates);
      return response.data.updated;
    } catch (error: any) {
      throw new Error(
        error.response?.data?.message ||
          error.message ||
          "Failed to update order"
      );
    }
  },

  // Delete order
  async deleteOrder(orderId: string): Promise<void> {
    try {
      await api.delete(`/${orderId}`);
    } catch (error: any) {
      throw new Error(
        error.response?.data?.message ||
          error.message ||
          "Failed to delete order"
      );
    }
  },

  // Get orders for a specific rider
  async getOrdersByRider(
    publicRiderId: string,
    page: number = 1,
    pageSize: number = 20,
    filters?: { dateFrom?: string; dateTo?: string; clientId?: string; clientName?: string; city?: string; sortBy?: string; sortOrder?: 'asc'|'desc' }
  ): Promise<OrdersResponse> {
    try {
      const params = new URLSearchParams();
      params.append("page", page.toString());
      params.append("pageSize", pageSize.toString());
      if (filters?.dateFrom) params.append("dateFrom", filters.dateFrom);
      if (filters?.dateTo) params.append("dateTo", filters.dateTo);
      if (filters?.clientId) params.append("clientId", filters.clientId);
      if (filters?.clientName) params.append("clientName", filters.clientName);
      if (filters?.city) params.append("city", filters.city);
      if (filters?.sortBy) params.append("sortBy", filters.sortBy);
      if (filters?.sortOrder) params.append("sortOrder", filters.sortOrder);

      const response = await api.get(
        `/rider/${publicRiderId}?${params.toString()}`
      );
      return response.data;
    } catch (error: any) {
      throw new Error(
        error.response?.data?.message ||
          error.message ||
          "Failed to fetch rider orders"
      );
    }
  },

  // Get order summary (total orders per rider)
  async getOrderSummary(): Promise<any[]> {
    try {
      const response = await api.get("/summary");
      return response.data;
    } catch (error: any) {
      throw new Error(
        error.response?.data?.message ||
          error.message ||
          "Failed to fetch order summary"
      );
    }
  },

  // Get city-wise summary
  async getCitySummary(filters?: {
    clientId?: string;
    riderId?: string;
    dateFrom?: string;
    dateTo?: string;
  }): Promise<CitySummaryResponse> {
    try {
      const params = new URLSearchParams();
      if (filters?.clientId) params.append("clientId", filters.clientId);
      if (filters?.riderId) params.append("riderId", filters.riderId);
      if (filters?.dateFrom) params.append("dateFrom", filters.dateFrom);
      if (filters?.dateTo) params.append("dateTo", filters.dateTo);

      const response = await api.get(`/city/summary?${params.toString()}`);
      return response.data;
    } catch (error: any) {
      throw new Error(
        error.response?.data?.message ||
          error.message ||
          "Failed to fetch city summary"
      );
    }
  },

  // Get orders by city
  async getOrdersByCity(
    city: string,
    filters?: {
      page?: number;
      pageSize?: number;
      riderId?: string;
      clientId?: string;
      dateFrom?: string;
      dateTo?: string;
    }
  ): Promise<OrdersResponse> {
    try {
      const params = new URLSearchParams();
      if (filters?.page) params.append("page", filters.page.toString());
      if (filters?.pageSize)
        params.append("pageSize", filters.pageSize.toString());
      if (filters?.riderId) params.append("riderId", filters.riderId);
      if (filters?.clientId) params.append("clientId", filters.clientId);
      if (filters?.dateFrom) params.append("dateFrom", filters.dateFrom);
      if (filters?.dateTo) params.append("dateTo", filters.dateTo);

      const response = await api.get(
        `/city/${encodeURIComponent(city)}?${params.toString()}`
      );
      return response.data;
    } catch (error: any) {
      throw new Error(
        error.response?.data?.message ||
          error.message ||
          "Failed to fetch city orders"
      );
    }
  },

  // Bulk upload orders (CSV/Excel)
  // If `background` is true, server will enqueue a job and return { jobId }
  async uploadOrders(file: File, background: boolean = true): Promise<any> {
    try {
      const formData = new FormData();
      formData.append("file", file);

      const url = background ? `/upload?background=true` : `/upload`;

      const response = await api.post(url, formData, {
        // Don't set Content-Type header - axios will automatically set it with correct boundary
        maxContentLength: Infinity,
        maxBodyLength: Infinity,
      });
      return response.data;
    } catch (error: any) {
      // If server returned a validation error payload, include it on the thrown error
      const message = error.response?.data?.message || error.message || "Failed to upload orders";
      const err: any = new Error(message);
      if (error.response?.data) err.responseData = error.response.data;
      if (error.response?.status) err.status = error.response.status;
      throw err;
    }
  },

  // Poll upload job status
  async getUploadStatus(jobId: string): Promise<any> {
    try {
      const response = await api.get(`/upload/status/${jobId}`);
      return response.data;
    } catch (error: any) {
      throw new Error(
        error.response?.data?.message || error.message || "Failed to get upload status"
      );
    }
  },
};

export default ordersService;

